import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  ShieldCheck, Boxes, BrainCircuit, Radar, ListChecks, FileText,
  TrendingUp, Wrench, Layers, ArrowRight, Check, Loader2, Mail,
} from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwxfHxJVCUyMGluZnJhc3RydWN0dXJlJTIwZGF0YSUyMGNlbnRlciUyMHNlcnZlcnN8ZW58MHx8fHwxNzgzNTQyMzc5fDA&ixlib=rb-4.1.0&q=85";

const MODULES = [
  { icon: Boxes, title: "Assessment Tool IT", desc: "Valuta lo stack IT dei clienti con criteri ponderati, punteggi e confronti oggettivi." },
  { icon: BrainCircuit, title: "AI & Sales Readiness", desc: "Framework a 5 aree pesate con maturity score /100 e radar di maturità." },
  { icon: Radar, title: "Gap Analysis AI", desc: "L'AI individua ridondanze, gap e raccomandazioni prioritizzate sullo stack." },
  { icon: ListChecks, title: "Roadmap & Deliverable AI", desc: "Priorità, quick win e roadmap a fasi generate automaticamente." },
  { icon: FileText, title: "Executive One-Pager & Report", desc: "Output cliente PDF brandizzati, con logo dello studio e co-branding partner." },
  { icon: TrendingUp, title: "Pipeline Commerciale / CRM", desc: "Traduce il punteggio readiness in offerta consigliata e valore stimato (ROI)." },
  { icon: Wrench, title: "Remediation Library", desc: "Catalogo di rimedi globale + custom, con generazione AI on-demand." },
  { icon: Layers, title: "SaaS Multi-Tenant", desc: "Ogni studio ha il proprio spazio isolato. Portale cliente in sola lettura." },
];

const STEPS = [
  { n: "01", title: "Valuta lo stack IT", desc: "Inserisci tool e processi del cliente e ottieni punteggi ponderati in pochi minuti." },
  { n: "02", title: "Genera la Gap Analysis", desc: "L'AI produce criticità, priorità e una roadmap a fasi pronta da discutere." },
  { n: "03", title: "Presenta il One-Pager", desc: "Consegna un report executive brandizzato e apri l'opportunità commerciale." },
];

const RequestInfoDialog = ({ testid, variant = "secondary", size = "sm" }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", company: "", email: "", website: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/leads", form);
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Invio non riuscito. Riprova.");
    } finally { setLoading(false); }
  };

  const reset = () => { setForm({ name: "", company: "", email: "", website: "" }); setDone(false); };

  const btnCls =
    variant === "inverse"
      ? "bg-transparent text-white border border-white hover:bg-white hover:text-primary"
      : "bg-white text-foreground border border-foreground hover:bg-foreground hover:text-white";

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        <Button
          data-testid={testid}
          className={`rounded-none font-mono uppercase tracking-[0.15em] transition-colors duration-200 ${
            size === "lg" ? "h-12 px-8 text-sm" : "h-10 px-6 text-xs"
          } ${btnCls}`}
        >
          Richiedi informazioni
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-none sm:max-w-md">
        {!done ? (
          <>
            <DialogHeader>
              <DialogTitle className="font-heading font-black tracking-tight">Richiedi informazioni</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-muted-foreground -mt-1">Lasciaci i tuoi dati: ti ricontatteremo al più presto.</p>
            <form onSubmit={submit} className="space-y-4 mt-2">
              {/* honeypot nascosto anti-spam */}
              <input type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className="hidden" aria-hidden="true" />
              <div>
                <Label className="data-label">Nome e cognome</Label>
                <Input data-testid="lead-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required minLength={2} className="mt-1 rounded-none" placeholder="Mario Rossi" />
              </div>
              <div>
                <Label className="data-label">Studio / Azienda</Label>
                <Input data-testid="lead-company-input" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} required className="mt-1 rounded-none" placeholder="Studio Rossi Consulting" />
              </div>
              <div>
                <Label className="data-label">Email</Label>
                <Input type="email" data-testid="lead-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className="mt-1 rounded-none" placeholder="nome@azienda.com" />
              </div>
              <DialogFooter>
                <Button type="submit" data-testid="lead-submit-btn" disabled={loading} className="rounded-none w-full font-mono uppercase tracking-[0.15em] bg-primary text-primary-foreground hover:bg-[#030712] transition-colors duration-200">
                  {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Invia richiesta
                </Button>
              </DialogFooter>
            </form>
          </>
        ) : (
          <div className="py-6 text-center" data-testid="lead-success">
            <div className="w-12 h-12 bg-primary mx-auto flex items-center justify-center"><Mail className="w-6 h-6 text-primary-foreground" /></div>
            <h3 className="mt-4 font-heading font-black tracking-tight text-xl">Richiesta inviata</h3>
            <p className="mt-2 text-sm text-muted-foreground">Grazie! Ti ricontatteremo presto all'indirizzo indicato.</p>
            <Button onClick={() => { setOpen(false); reset(); }} className="mt-6 rounded-none font-mono uppercase tracking-[0.15em] bg-primary text-primary-foreground hover:bg-[#030712] transition-colors duration-200">Chiudi</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const AccediButton = ({ testid, variant = "primary", size = "lg", children }) => (
  <Button
    asChild
    data-testid={testid}
    className={`rounded-none font-mono uppercase tracking-[0.15em] transition-colors duration-200 ${
      size === "lg" ? "h-12 px-8 text-sm" : "h-10 px-6 text-xs"
    } ${
      variant === "primary"
        ? "bg-primary text-primary-foreground hover:bg-[#030712]"
        : variant === "inverse"
        ? "bg-white text-primary hover:bg-[#030712] hover:text-white"
        : "bg-white text-foreground border border-foreground hover:bg-foreground hover:text-white"
    }`}
  >
    <Link to="/login">{children}</Link>
  </Button>
);

export default function Landing() {
  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="landing-page">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-white border-b border-foreground">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-primary flex items-center justify-center"><ShieldCheck className="w-5 h-5 text-primary-foreground" /></div>
            <span className="font-heading font-black tracking-tight text-lg">ITASSESS</span>
          </div>
          <div className="flex items-center gap-3">
            <RequestInfoDialog testid="header-request-info-button" variant="secondary" size="sm" />
            <AccediButton testid="header-accedi-button" size="sm">Accedi</AccediButton>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="border-b border-foreground">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2">
          <div className="px-6 lg:px-10 py-20 lg:py-32 flex flex-col justify-center border-r-0 lg:border-r border-foreground fade-up">
            <div className="data-label text-primary mb-6">Piattaforma per studi di consulenza IT</div>
            <h1 className="font-heading font-black tracking-tighter text-5xl sm:text-6xl lg:text-7xl leading-none">
              Eleva la consulenza IT con l'intelligenza artificiale.
            </h1>
            <p className="mt-8 text-base md:text-lg text-muted-foreground max-w-xl leading-relaxed">
              Assessment strutturato dei tool IT, AI &amp; Sales Readiness, gap analysis e pipeline commerciale.
              Dal punteggio al one-pager brandizzato: tutto ciò che serve per aprire e chiudere il progetto.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <AccediButton testid="hero-accedi-button">Accedi alla piattaforma <ArrowRight className="w-4 h-4 ml-2" /></AccediButton>
              <a href="#moduli" className="font-mono uppercase tracking-[0.15em] text-xs text-muted-foreground hover:text-foreground transition-colors duration-200 border-b border-transparent hover:border-foreground pb-0.5">Scopri i moduli</a>
            </div>
          </div>
          <div className="relative min-h-[320px] lg:min-h-full">
            <img src={HERO_IMG} alt="Infrastruttura IT" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-primary/85" />
            <div className="relative h-full flex flex-col justify-end p-8 lg:p-12 text-primary-foreground">
              <div className="grid grid-cols-3 gap-px bg-white/20 border border-white/20">
                {[["8", "Moduli"], ["/100", "Maturity"], ["AI", "Native"]].map(([v, l]) => (
                  <div key={l} className="bg-primary px-3 py-5 text-center">
                    <div className="font-heading font-black text-3xl">{v}</div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-primary-foreground/70 mt-1">{l}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features grid */}
      <section id="moduli" className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12">
          <div>
            <div className="data-label text-primary mb-3">Cosa include</div>
            <h2 className="font-heading font-black tracking-tighter text-4xl sm:text-5xl leading-tight">Otto moduli, un'unica<br className="hidden sm:block" /> piattaforma consulenziale.</h2>
          </div>
          <p className="text-muted-foreground max-w-sm">Progettata per il flusso di lavoro reale di uno studio: dalla valutazione tecnica alla proposta commerciale.</p>
        </div>
        <div data-testid="features-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 border-t border-l border-foreground">
          {MODULES.map((m, i) => {
            const Icon = m.icon;
            return (
              <div key={m.title} className="group border-r border-b border-foreground p-8 bg-white hover:bg-primary transition-colors duration-200 fade-up" style={{ animationDelay: `${i * 60}ms` }}>
                <div className="flex items-center justify-between">
                  <Icon className="w-7 h-7 text-primary group-hover:text-white transition-colors duration-200" strokeWidth={1.5} />
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground group-hover:text-white/70 transition-colors duration-200">Modulo {String(i + 1).padStart(2, "0")}</span>
                </div>
                <h3 className="mt-6 font-heading font-black tracking-tight text-lg group-hover:text-white transition-colors duration-200">{m.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground group-hover:text-white/80 transition-colors duration-200 leading-relaxed">{m.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* How it works */}
      <section data-testid="how-it-works-section" className="border-y border-foreground bg-[#F9FAFB]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <div className="data-label text-primary mb-3">Come funziona</div>
          <h2 className="font-heading font-black tracking-tighter text-4xl sm:text-5xl leading-tight mb-14">Dal dato al deliverable in tre passi.</h2>
          <div className="grid md:grid-cols-3 border-t border-l border-foreground">
            {STEPS.map((s) => (
              <div key={s.n} className="border-r border-b border-foreground p-8 lg:p-10 bg-white">
                <div className="font-heading font-black text-primary text-6xl lg:text-7xl leading-none tracking-tighter">{s.n}</div>
                <h3 className="mt-6 font-heading font-black tracking-tight text-xl">{s.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Value prop */}
      <section data-testid="value-prop-section" className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="data-label text-primary mb-3">Costruito per gli studi</div>
            <h2 className="font-heading font-black tracking-tighter text-4xl sm:text-5xl leading-tight">Multi-tenant, isolato, white-label.</h2>
            <p className="mt-6 text-muted-foreground leading-relaxed max-w-lg">Ogni studio di consulenza lavora nel proprio spazio isolato. Personalizza i report con il tuo brand e coinvolgi i clienti con un portale in sola lettura.</p>
            <ul className="mt-8 space-y-3">
              {["Isolamento dati per studio (org)", "Report e one-pager brandizzati", "Ruoli team: Admin, Membro, Sola lettura", "AI integrata su assessment e remediation"].map((f) => (
                <li key={f} className="flex items-center gap-3 text-sm">
                  <span className="w-5 h-5 bg-primary flex items-center justify-center shrink-0"><Check className="w-3 h-3 text-white" /></span>
                  {f}
                </li>
              ))}
            </ul>
          </div>
          <div className="border border-foreground bg-primary p-10 lg:p-14 text-primary-foreground">
            <div className="font-heading font-black tracking-tighter text-3xl leading-tight">"Trasforma un assessment in una proposta commerciale, senza copiaincolla."</div>
            <div className="mt-8 data-label text-primary-foreground/60">Il flusso di lavoro dello studio, in un unico posto</div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-primary text-primary-foreground border-y border-foreground">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32 text-center">
          <h2 className="font-heading font-black tracking-tighter text-4xl sm:text-6xl leading-none max-w-3xl mx-auto">Inizia a trasformare la tua consulenza oggi.</h2>
          <p className="mt-6 text-primary-foreground/80 max-w-xl mx-auto">Accedi alla piattaforma con le credenziali fornite dal tuo studio.</p>
          <div className="mt-10 flex flex-wrap justify-center items-center gap-4">
            <AccediButton testid="footer-accedi-button" variant="inverse">Accedi ora <ArrowRight className="w-4 h-4 ml-2" /></AccediButton>
            <RequestInfoDialog testid="footer-request-info-button" variant="inverse" size="lg" />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer data-testid="footer" className="bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-primary flex items-center justify-center"><ShieldCheck className="w-4 h-4 text-primary-foreground" /></div>
            <span className="font-heading font-black tracking-tight text-sm">ITASSESS</span>
          </div>
          <div className="data-label">© {new Date().getFullYear()} ITASSESS — Piattaforma di assessment per studi IT</div>
        </div>
      </footer>
    </div>
  );
}
